import {
  Button,
  Container,
  Form,
  Frequency,
  Input,
  Message,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { FormActions } from "components/FormActions/FormActions";
import { Cancel } from "components/IncreaseCreditLimit/Cancel";
import { CancelLink } from "components/IncreaseCreditLimit/components/CancelLink";
import { Layout } from "components/Layout/Layout";
import { Separator } from "components/Separator/Separator";
import { formatAmount } from "library/moneyUtils";
import { yesNoOptions } from "library/sharedOptions";
import {
  nzMortgageMaxValue,
  nzTotalExpensesMaxValue,
} from "pages/IncreaseCreditLimit/constants/constants";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCliContext } from "../hooks/useCliContext";
import {
  ActionTypes,
  ExpensesFrequencyValue,
  MonthlyExpensesType,
} from "../types";
import {
  checkAllExpensesFrequencyValues,
  getTotalMonthlyExpenses,
  isInvalidField,
} from "../utils";
import {
  nzValidateRadioGroupField,
  validateFrequencyField,
} from "../validation/nzCustomValidation";

export const Expenses = () => {
  const {
    state: { accountInfo, monthlyExpenses },
    dispatch,
  } = useCliContext();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { methods, Field } = useBasisForm<MonthlyExpensesType>({
    defaultValues: monthlyExpenses,
  });
  const {
    watch,
    formState: { errors },
  } = methods;

  const {
    nzHaveOffsetMortgage,
    nzAllExpensesForHousehold,
    expensesFrequency,
    ...formValues
  } = watch();

  const {
    nzFoodGroceriesEntertainment,
    nzGeneralInsurance,
    nzTransportAuto,
    nzPersonalFitnessMedical,
    nzMortgage,
  } = expensesFrequency;

  const onSubmit = (values: MonthlyExpensesType) => {
    if (!showHardStop) {
      dispatch({
        type: ActionTypes.SUBMIT_EXPENSES,
        monthlyExpenses: {
          ...values,
          nzTotalExpenses: totalExpenses,
        },
      });
      navigate("/increase-credit-limit/additional-debts");
    }
  };

  const totalExpenses = getTotalMonthlyExpenses(
    expensesFrequency as ExpensesFrequencyValue,
    errors,
  );

  const showSoftStop =
    [
      nzFoodGroceriesEntertainment.amount,
      nzGeneralInsurance.amount,
      nzTransportAuto.amount,
      nzPersonalFitnessMedical.amount,
      nzMortgage.amount,
    ].some((field) => isInvalidField(field)) ||
    isInvalidField(nzMortgage.amount, nzMortgageMaxValue) ||
    isInvalidField(totalExpenses, nzTotalExpensesMaxValue);

  const showHardStop: boolean =
    checkAllExpensesFrequencyValues(
      expensesFrequency as ExpensesFrequencyValue,
      true,
    ) && totalExpenses === 0;

  const showTotalMonthlyExpensesValue =
    totalExpenses === null
      ? "–"
      : formatAmount(totalExpenses, { withCents: true });

  return (
    <Layout>
      <Layout.Header>Request a credit limit increase</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo?.cardName ?? ""}
                cardEndsWith={accountInfo?.cardEndsWith}
              />
            </Container>
            <Stack gap="4">
              <Text>
                <strong>Step 3 of 3</strong>
              </Text>
              <Text as="h3" textStyle="heading4">
                Your expenses
              </Text>
            </Stack>
            <Separator />
            <Stack gap="8" width="100%" width-sm="400">
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Your monthly expenses
              </Text>
              <Field
                as={RadioGroup}
                name="nzAllExpensesForHousehold"
                label="Do you pay all the expenses for your household?"
                options={yesNoOptions}
                validate={nzValidateRadioGroupField}
              />
              {nzAllExpensesForHousehold === "no" && (
                <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
                  Only include your share of living expenses each month.
                </Message>
              )}
              <Field
                as={RadioGroup}
                name="nzHaveOffsetMortgage"
                label="Do you have a revolving or offset mortgage?"
                options={yesNoOptions}
                helpText="Revolving credit allows a customer the flexibility to access money up to a preset amount known as the credit limit. Unlike more traditional home loans, customers with a revolving facility can draw down, repay and redraw funds at any time."
                validate={nzValidateRadioGroupField}
              />
              {nzHaveOffsetMortgage === "yes" && (
                <Field
                  as={Input}
                  name="nzRevolvingMortgage"
                  label="What is your facility limit?"
                  variant="numeric"
                  prefix="$"
                  maxLength="8"
                />
              )}
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Your residential expenses
              </Text>

              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzMortgage"
                selectPlaceholder=""
                mode="select"
                label="Mortgage, rent or board"
                helpText="For your primary residence"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzSecondaryMortgage"
                selectPlaceholder=""
                label="Secondary Property Mortgage, rent or board "
                helpText="For any secondary or investment property"
                mode="select"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzPropertyRates"
                selectPlaceholder=""
                mode="select"
                label="Property rates"
                helpText="Property rates for both primary and secondary properties"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzOtherPropertyExpenses"
                selectPlaceholder=""
                label="Utilities & Other Property Expenses"
                helpText="e.g. Body corporate fees, water, rubbish collection, recycling, general property maintenance not included in above"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Separator />
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Your living expenses
              </Text>
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzFoodGroceriesEntertainment"
                selectPlaceholder=""
                label="Food & Groceries & Entertainment"
                helpText="e.g. Supermarket, bakery, restaurants, takeaways, lunches, coffees"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzPersonalInsurance"
                selectPlaceholder=""
                label="Health and Life Insurance"
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzGeneralInsurance"
                selectPlaceholder=""
                label="General Insurance"
                helpText="e.g. Home, vehicle and contents insurance etc."
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzTransportAuto"
                selectPlaceholder=""
                label="Transport & Auto"
                helpText="e.g. public transport, ride sharing, petrol, parking, vehicle maintenance and registration etc."
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzEducationChildcare"
                selectPlaceholder=""
                label="Education & Childcare"
                helpText="e.g. Private school and higher education fees and daycare etc."
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzPersonalFitnessMedical"
                selectPlaceholder=""
                label="Personal, Fitness & Medical"
                helpText="e.g. phone, clothing, medical, fitness, vet and doctor etc."
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Field
                as={Frequency}
                amountMaxLength="8"
                name="expensesFrequency.nzAnyAdditionalExpense"
                selectPlaceholder=""
                label="Any Additional expenses"
                helpText="e.g. child maintenance, donations and tithings to family and/or the church etc."
                amountPrefix="$"
                annually={false}
                validate={validateFrequencyField}
                mode="select"
              />
              <Text>
                <strong>
                  Your total monthly expenses: {showTotalMonthlyExpensesValue}
                </strong>
              </Text>
              {!showHardStop && showSoftStop && (
                <Message
                  severity="warning-or-significant"
                  bg="secondary.lightBlue.t25"
                  title="Are you all details correct?"
                >
                  Please review the following details before you continue:
                  <br />
                  <br />
                  {isInvalidField(nzMortgage.amount) && (
                    <MessageWrapper>Mortgage, rent, board</MessageWrapper>
                  )}
                  {isInvalidField(nzFoodGroceriesEntertainment.amount) && (
                    <MessageWrapper>
                      Food & Groceries & Entertainment
                    </MessageWrapper>
                  )}
                  {isInvalidField(nzGeneralInsurance.amount) && (
                    <MessageWrapper>General Insurance</MessageWrapper>
                  )}
                  {isInvalidField(nzTransportAuto.amount) && (
                    <MessageWrapper>Transport & Auto</MessageWrapper>
                  )}
                  {isInvalidField(nzPersonalFitnessMedical.amount) && (
                    <MessageWrapper>Personal, Fitness & Medical</MessageWrapper>
                  )}
                  {isInvalidField(totalExpenses, nzTotalExpensesMaxValue) && (
                    <MessageWrapper>
                      Total monthly residential and living expenses more than{" "}
                      {formatAmount(nzTotalExpensesMaxValue)}
                    </MessageWrapper>
                  )}
                  {isInvalidField(nzMortgage.amount, nzMortgageMaxValue) && (
                    <MessageWrapper>
                      Mortgage, rent or board is more than{" "}
                      {formatAmount(nzMortgageMaxValue)}
                    </MessageWrapper>
                  )}
                  <br />
                  <span>
                    <strong>
                      By continue you are confirming these are correct
                    </strong>
                  </span>
                </Message>
              )}
              {showHardStop && (
                <Message
                  severity="warning-or-significant"
                  bg="secondary.pink.t30"
                >
                  <strong>
                    Total residential and living expenses must be greater than
                    $0
                  </strong>
                </Message>
              )}
              <FormActions>
                <FormActions.Primary>
                  <Button variant="primary" type="submit">
                    Next
                  </Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      dispatch({
                        type: ActionTypes.SUBMIT_EXPENSES,
                        monthlyExpenses: {
                          ...formValues,
                          expensesFrequency,
                          nzAllExpensesForHousehold,
                          nzHaveOffsetMortgage,
                          nzTotalExpenses: totalExpenses,
                        },
                      });
                      navigate(-1);
                    }}
                  >
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
              <CancelLink onClick={() => setIsModalOpen(true)} />
            </Stack>
            <Cancel
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                navigate("/manage-account");
              }}
            />
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};

const MessageWrapper = ({ children }: { children: ReactNode }) => (
  <span style={{ display: "list-item", marginLeft: 30 }}>{children}</span>
);
